import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CdkTableModule } from '@angular/cdk/table';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ColorPickerComponent, ParameterValueComponent } from '@vectronic/ui';
import { MobileService, TableCollapseService, Translator } from '@vectronic/util';
import { TableVirtualScrollDataSource, TableVirtualScrollModule } from 'ng-table-virtual-scroll';
import { of, tap, withLatestFrom } from 'rxjs';
import {
  DataForwardingChipsCellComponent,
  DatatableAutocompleteInputComponent,
  DatatableBeltComponent,
  DatatableCheckboxComponent,
  DatatableCheckboxToggleEvent,
  DatatableCommandContentComponent,
  DatatableDateWithRelativeWeeksComponent,
  DatatableDisabledSubsystemsComponent,
  DatatableDropOffComponent,
  DatatableDropOffStringComponent,
  DatatableEditCheckboxComponent,
  DatatableFirmwareComponent,
  DatatableGroupsCheckboxComponent,
  DatatableIdsTooltipComponent,
  DatatableInputComponent,
  DatatableJsonComponent,
  DatatableListWithActionsComponent,
  DatatableMovebankSwitchComponent,
  DatatableMultiValuesContentComponent,
  DatatableParameterComponent,
  DatatableRmaDescriptionComponent,
  DatatableRmaDeviceSelectorComponent,
  DatatableSchedulesComponent,
  DatatableStatusSelectComponent,
  DatatableStringWithActionComponent,
  DatatableSvgComponent,
  DatatableUsersCountComponent,
  GroupChipsCellComponent,
  MemberChipsCellComponent,
  MovebankForwardingChipsCellComponent,
  MultiRoleSelectorComponent,
  NotificationChipsCellComponent,
  RoleSelectorComponent,
} from '../../column-components';
import { DatatableTooltipPipe, DatatableValuePipe } from '../../pipes/datatable-value.pipe';
import { DatatableDataPageService } from '../../services/datatable-data-page.service';
import { TableSelectionService } from '../../services/table-selection.service';
import {
  componentsThatEmitOnClick,
  DatatableColumn,
  DatatableColumnIcon,
  DisplayColumn,
} from '../../types/datatable-column';
import { DatatableConfig } from '../../types/datatable-configuration';
import { DatatableHeaderCheckboxComponent } from '../datatable-header-checkbox/datatable-header-checkbox.component';
import { DatatableToggleComponent } from '../datatable-toggle/datatable-toggle.component';
import { RowActionsComponent } from '../row-action/row-actions.component';
import { DatatableRangeSelectionController } from './datatable-range-selection.controller';

@Component({
  selector: 'table-datatable-body',
  templateUrl: './datatable-body.component.html',
  styleUrls: ['./datatable-body.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatProgressBarModule,
    ScrollingModule,
    TableVirtualScrollModule,
    CdkTableModule,
    DragDropModule,
    MatTableModule,
    MatSortModule,
    DatatableHeaderCheckboxComponent,
    DatatableDropOffComponent,
    DatatableDropOffStringComponent,
    DatatableBeltComponent,
    DatatableMovebankSwitchComponent,
    DatatableCheckboxComponent,
    DatatableUsersCountComponent,
    DatatableIdsTooltipComponent,
    DatatableGroupsCheckboxComponent,
    RoleSelectorComponent,
    MultiRoleSelectorComponent,
    DatatableListWithActionsComponent,
    DatatableValuePipe,
    DatatableTooltipPipe,
    GroupChipsCellComponent,
    NotificationChipsCellComponent,
    DataForwardingChipsCellComponent,
    MovebankForwardingChipsCellComponent,
    MemberChipsCellComponent,
    DatatableJsonComponent,
    DatatableParameterComponent,
    DatatableDisabledSubsystemsComponent,
    DatatableSchedulesComponent,
    DatatableStringWithActionComponent,
    RowActionsComponent,
    DatatableCommandContentComponent,
    DatatableMultiValuesContentComponent,
    DatatableToggleComponent,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatFormFieldModule,
    ColorPickerComponent,
    DatatableSvgComponent,
    DatatableFirmwareComponent,
    DatatableEditCheckboxComponent,
    DatatableDateWithRelativeWeeksComponent,
    ParameterValueComponent,
    DatatableRmaDescriptionComponent,
    DatatableRmaDeviceSelectorComponent,
    DatatableStatusSelectComponent,
    DatatableAutocompleteInputComponent,
    DatatableInputComponent,
    MatMenuModule,
  ],
  providers: [DatatableTooltipPipe],
})
export class DatatableBodyComponent<T extends object> implements OnInit, OnDestroy, AfterViewInit {
  tableSelectionService = inject(TableSelectionService);
  private tableCollapseService = inject(TableCollapseService);
  private el = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  private datatableDataPageService = inject(DatatableDataPageService);
  private datatableTooltipPipe = inject(DatatableTooltipPipe);
  mobileService = inject(MobileService);

  datatableConfig = input<DatatableConfig<T>>({} as any);
  displayColumns = input<DisplayColumn<T>[]>();
  data = input<T[]>([]);
  isLoading = input<boolean>();
  loadingProgress = input<number>();
  pageIndex = input(0);
  pageSize = input(0);
  isCompactModeActivated = input<boolean>();

  reorderColumns = output<CdkDragDrop<string[]>>();
  sortTable = output<Sort>();
  rowClicked = output<T>();

  @ViewChild('virtualScrollViewport')
  virtualScrollViewport: CdkVirtualScrollViewport;

  @ViewChild(MatMenuTrigger) contextMenuTrigger: MatMenuTrigger;

  dataSource = new TableVirtualScrollDataSource<T>();

  trackByFn = (index: number, item: T) => this.datatableConfig()?.trackBy?.(index, item) ?? item;
  itemHeight = computed(() => (this.isCompactModeActivated() ? 23 : 52));
  selectedRowForContextMenu = signal<T | undefined>(undefined);
  contextMenuPosition = signal({ x: '0px', y: '0px' });
  sortConfig = computed(() => this.datatableConfig()?.sort);
  private paginationConfig = computed(() => this.datatableConfig()?.pagination);
  readonly cellTooltipTop = signal(0);
  readonly cellTooltipLeft = signal(0);
  readonly cellTooltipAbove = signal(false);
  readonly cellTooltipVisible = signal(false);
  readonly cellTooltipClass = signal(undefined);
  readonly cellTooltipText = signal('');
  readonly cellTooltipComponent = signal<any>(undefined);
  readonly cellTooltipRow = signal<any>(undefined);

  lastScrollPosition = 0;

  private observer = new IntersectionObserver(() => {
    this.virtualScrollViewport?.checkViewportSize();
  });

  displayColumnsWithActions = computed(() => {
    let displayColumns = this.displayColumns();
    if (this.isCompactModeActivated()) {
      displayColumns = displayColumns.filter((displayColumn) =>
        this.datatableConfig().columns?.find(
          ({ hideInCompactMode, fieldName }) => displayColumn === fieldName && !hideInCompactMode,
        ),
      );
    }
    const hasCheckBoxColumn = this.datatableConfig().columns?.some(
      ({ fieldName }) => fieldName === '#',
    );
    const hasAction = this.datatableConfig().rowActions?.length > 0;
    if (hasCheckBoxColumn && !displayColumns.includes('#')) displayColumns.unshift('#');
    if (hasAction && !displayColumns.includes('rowActions')) displayColumns.push('rowActions');
    return displayColumns;
  });

  sortSignal = signal<Sort>(undefined);
  private rangeSelectionController = new DatatableRangeSelectionController<string | number>();

  private didApplyInitialSelection = signal(false);
  private applyInitialSelectionEffect = effect(() => {
    const { initial } = this.datatableConfig()?.select ?? {};
    if (this.didApplyInitialSelection() || !initial?.length) return;

    this.tableSelectionService.selectMultiple(initial);
    this.didApplyInitialSelection.set(true);
  });

  sortedData = computed(() => {
    let data = this.data();
    if (!data?.length) return [];
    const config = this.datatableConfig();
    const sortConfig = this.sortConfig();
    if (sortConfig?.internal) {
      const sortSignal = this.sortSignal();
      if (sortSignal) data = this.sortData(sortSignal, data);
      else if (sortConfig.initial) {
        const { column, direction } = sortConfig.initial;
        data = this.sortData({ active: column, direction }, data);
      }
    }
    if (this.didApplyInitialSelection() && !config.select?.disableSortInitialToTop) {
      const key = config.select?.key ?? config.select?.multi;
      data = data.toSorted((a, b) => {
        const isASelected = config.select?.initial?.includes(a[key] as any);
        const isBSelected = config.select?.initial?.includes(b[key] as any);
        if (isASelected && !isBSelected) return -1;
        if (!isASelected && isBSelected) return 1;
        return 0;
      });
    }
    return data;
  });

  dataPage = computed(() => {
    const data = this.sortedData();
    if (!this.paginationConfig()?.internal) return data;
    const pageIndex = this.pageIndex();
    const pageSize = this.pageSize();
    const startIndex = pageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  });

  settingDataEffect = effect(() => {
    const dataPage = this.dataPage();
    this.dataSource.data = dataPage;
    this.datatableDataPageService.dataPage.set(dataPage);
  });
  headerNameEffect = effect(() => {
    for (const column of this.datatableConfig().columns ?? []) {
      column.headerName ??= Translator.headerName(column.fieldName);
    }
  });
  private resetRangeAnchorOnEmptySelection = effect(() => {
    if (this.tableSelectionService.selectionLength() === 0) {
      this.rangeSelectionController.resetAnchors();
    }
  });
  private highlightSelectionAnchor?: T;
  private highlightDeselectionAnchor?: T;
  private resetMultiHighlightAnchor = effect(() => {
    if (this.tableSelectionService.highlightedRows?.()?.length === 0) {
      this.highlightSelectionAnchor = undefined;
      this.highlightDeselectionAnchor = undefined;
    }
  });

  ngOnInit() {
    this.initSelection();
    const { doHighlight, doMultiHighlight, key, initialHighlight } =
      this.datatableConfig()?.select ?? {};
    if (doHighlight) {
      this.tableSelectionService.highlightedRow.set(undefined);
      if (initialHighlight != null) {
        this.tableSelectionService.highlightedRow.set({ [key as string]: initialHighlight });
      }
    }
    if (doMultiHighlight) {
      this.tableSelectionService.highlightedRows.set([]);
      this.tableSelectionService.highlightedRow.set(null);
      this.highlightSelectionAnchor = undefined;
      this.highlightDeselectionAnchor = undefined;
    }

    this.tableCollapseService.onSelectionChanged$
      ?.pipe(
        withLatestFrom(this.virtualScrollViewport?.scrolledIndexChange || of(undefined)),
        tap(([, scrollIndex]) => (this.lastScrollPosition = scrollIndex)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private initSelection() {
    const { key, single, multi, initial, doHighlight, doMultiHighlight } =
      this.datatableConfig()?.select ?? {};
    if (doHighlight || doMultiHighlight) return;
    if (key || single) this.tableSelectionService.singleSelection.clear();
    if (key || multi) this.tableSelectionService.selection.clear();
    if (initial?.length) {
      this.tableSelectionService.selectMultiple(initial);
      this.didApplyInitialSelection.set(true);
    }
  }

  ngAfterViewInit() {
    this.observer.observe(this.el.nativeElement);
  }

  emitSortTable(event: Sort) {
    if (this.sortConfig()?.internal) this.sortSignal.set(event);
    this.sortTable.emit(event);
  }

  onCheckboxToggle(
    event: DatatableCheckboxToggleEvent,
    row: T,
    checkboxToggle?: (event: DatatableCheckboxToggleEvent, row: T) => void,
  ) {
    this.handleRangeCheckboxToggle(event);
    checkboxToggle?.(event, row);
  }

  onRowMouseDown(event: MouseEvent) {
    if (!this.datatableConfig()?.select?.doMultiHighlight) return;
    const { button, ctrlKey, metaKey, shiftKey, target } = event;
    if (button !== 0) return;
    if (!ctrlKey && !metaKey && !shiftKey) return;
    if (
      target instanceof HTMLElement &&
      target.closest('input, textarea, mat-select, button, a, mat-checkbox')
    ) {
      return;
    }
    event.preventDefault();
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  handleCellClick(column: DatatableColumn<T>, dataRow: T, event: MouseEvent) {
    const { doHighlight, doMultiHighlight, multi, canSelect } =
      this.datatableConfig()?.select ?? {};
    if (doMultiHighlight && (event.shiftKey || event.ctrlKey || event.metaKey)) {
      window.getSelection()?.removeAllRanges();
    } else if (window.getSelection()?.toString().length) {
      return;
    }
    const isComponent = Boolean(column.component);
    const selectionAllowed = !canSelect || canSelect();
    if (!isComponent && column.onClick) {
      column.onClick(dataRow);
      event.stopPropagation();
    } else if (!isComponent || componentsThatEmitOnClick.includes(column.component?.name)) {
      if (selectionAllowed) {
        if (doMultiHighlight) {
          this.handleMultiHighlight(dataRow, event);
        } else if (doHighlight) {
          this.tableSelectionService.highlightedRow.set(dataRow);
        } else {
          const selectedId = this.getSingleSelectedId(dataRow);
          if (selectedId != null) this.tableSelectionService.singleSelection.toggle(selectedId);
          if (multi) {
            this.tableSelectionService.toggleCheckbox(this.getMultiSelectedId(dataRow));
          }
        }
      }
      this.rowClicked.emit(dataRow);
    }
  }

  private getNearestHighlightedRow(row: T, sortedData: T[], highlightedSet: Set<T>) {
    const currentIndex = sortedData.indexOf(row);
    if (currentIndex === -1) return undefined;
    for (let distance = 1; distance < sortedData.length; distance++) {
      const before = sortedData[currentIndex - distance];
      if (before && highlightedSet.has(before)) return before;
      const after = sortedData[currentIndex + distance];
      if (after && highlightedSet.has(after)) return after;
    }
    return undefined;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private handleMultiHighlight(dataRow: T, event: MouseEvent) {
    const isControlOrMeta = event.ctrlKey || event.metaKey;
    const isShift = event.shiftKey;
    const currentHighlighted = this.tableSelectionService.highlightedRows();
    const highlightedSet = this.tableSelectionService.highlightedRowsSet();
    const isCurrentlyHighlighted = highlightedSet.has(dataRow);
    const sortedData = this.sortedData();

    if (!isControlOrMeta && !isShift) {
      this.tableSelectionService.highlightedRows.set([dataRow]);
      this.tableSelectionService.highlightedRow.set(dataRow);
      this.highlightSelectionAnchor = dataRow;
      this.highlightDeselectionAnchor = undefined;
      return;
    }

    if (isShift) {
      const checked = !isCurrentlyHighlighted;

      if (!checked) {
        if (this.highlightDeselectionAnchor == null) {
          this.highlightDeselectionAnchor = dataRow;
          const nextRows = currentHighlighted.filter((row) => row !== dataRow);
          if (this.highlightSelectionAnchor === dataRow) {
            this.highlightSelectionAnchor = this.getNearestHighlightedRow(
              dataRow,
              sortedData,
              new Set(nextRows),
            );
          }
          this.tableSelectionService.highlightedRows.set(nextRows);
          this.tableSelectionService.highlightedRow.set(nextRows.at(-1) ?? null);
          return;
        }

        const anchor = this.highlightDeselectionAnchor;
        const anchorIndex = sortedData.indexOf(anchor);
        const currentIndex = sortedData.indexOf(dataRow);
        if (anchorIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(anchorIndex, currentIndex);
          const end = Math.max(anchorIndex, currentIndex);
          const range = sortedData.slice(start, end + 1);
          const rangeSet = new Set(range);
          const nextRows = currentHighlighted.filter((row) => !rangeSet.has(row));
          this.highlightDeselectionAnchor = anchor;
          if (this.highlightSelectionAnchor && rangeSet.has(this.highlightSelectionAnchor)) {
            this.highlightSelectionAnchor = this.getNearestHighlightedRow(
              dataRow,
              sortedData,
              new Set(nextRows),
            );
          }
          this.tableSelectionService.highlightedRows.set(nextRows);
          this.tableSelectionService.highlightedRow.set(nextRows.at(-1) ?? null);
          return;
        }
      }

      this.highlightDeselectionAnchor = undefined;
      const anchor =
        (this.highlightSelectionAnchor && highlightedSet.has(this.highlightSelectionAnchor)
          ? this.highlightSelectionAnchor
          : undefined) ??
        this.getNearestHighlightedRow(dataRow, sortedData, highlightedSet) ??
        dataRow;

      const anchorIndex = sortedData.indexOf(anchor);
      const currentIndex = sortedData.indexOf(dataRow);
      if (anchorIndex !== -1 && currentIndex !== -1) {
        const start = Math.min(anchorIndex, currentIndex);
        const end = Math.max(anchorIndex, currentIndex);
        const range = sortedData.slice(start, end + 1);
        const nextRows = Array.from(new Set([...currentHighlighted, ...range]));
        this.highlightSelectionAnchor = anchor;
        this.tableSelectionService.highlightedRows.set(nextRows);
        this.tableSelectionService.highlightedRow.set(dataRow);
        return;
      }
    }

    if (isControlOrMeta) {
      if (isCurrentlyHighlighted) {
        const nextRows = currentHighlighted.filter((row) => row !== dataRow);
        this.tableSelectionService.highlightedRows.set(nextRows);
        const lastRow = nextRows.at(-1);
        this.highlightDeselectionAnchor = dataRow;
        this.highlightSelectionAnchor = lastRow;
        this.tableSelectionService.highlightedRow.set(lastRow ?? null);
      } else {
        const nextRows = [...currentHighlighted, dataRow];
        this.tableSelectionService.highlightedRows.set(nextRows);
        this.highlightSelectionAnchor = dataRow;
        this.highlightDeselectionAnchor = undefined;
        this.tableSelectionService.highlightedRow.set(dataRow);
      }
    }
  }

  onRightClick(event: MouseEvent, row: T) {
    if (
      this.datatableConfig().disableRowActionContextMenu ||
      !this.datatableConfig().rowActions?.length ||
      window.getSelection()?.toString().length
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (this.contextMenuTrigger.menuOpen) this.contextMenuTrigger.closeMenu();
    this.selectedRowForContextMenu.set(undefined);
    this.contextMenuPosition.set({ x: event.clientX + 'px', y: event.clientY + 'px' });
    this.selectedRowForContextMenu.set(row);
    this.contextMenuTrigger.openMenu();
  }

  @HostListener('document:click')
  onDocumentClick() {
    if (this.contextMenuTrigger.menuOpen) this.contextMenuTrigger.closeMenu();
  }

  onScroll(scrollPosition: number) {
    if (
      this.datatableConfig().uniqueId === 'collars_inventa' &&
      Math.abs(scrollPosition - this.lastScrollPosition) > 4
    ) {
      this.tableCollapseService.setNotificationSelectedCollarId(undefined);
      this.tableCollapseService.setGroupSelectedMemberId(undefined);
    }
  }

  getRowStyle(dataRow: T, index: number) {
    let style = index % 2 === 0 ? {} : { 'background-color': '#f6f6f6' };
    if (this.datatableConfig().rowStyle) {
      style = { ...style, ...this.datatableConfig().rowStyle(dataRow, this.sortedData()) };
    }
    return style;
  }

  ngOnDestroy() {
    this.observer.unobserve(this.el.nativeElement);
  }

  private sortData({ active: orderBy, direction: orderDirection }: Sort, data: T[]) {
    if (!orderDirection || !orderBy) return data;
    const { sortValue, translator } =
      this.datatableConfig().columns.find(({ fieldName }) => orderBy === fieldName) ?? {};
    return data.toSorted((_a, _b) => {
      const a = sortValue?.(_a) ?? translator?.(_a) ?? _a[orderBy];
      const b = sortValue?.(_b) ?? translator?.(_b) ?? _b[orderBy];
      const isAsc = orderDirection === 'asc';
      return isAsc ? this.compare(a, b) : this.compare(b, a);
    });
  }

  private compare = (
    a: string | number | boolean | Date | null | undefined,
    b: string | number | boolean | Date | null | undefined,
  ) => {
    if (a instanceof Date) a = a.toISOString();
    if (b instanceof Date) b = b.toISOString();
    if (typeof a === 'number' || typeof a === 'boolean') a = a.toString();
    if (typeof b === 'number' || typeof b === 'boolean') b = b.toString();
    if (typeof a === 'object') a = '';
    if (typeof b === 'object') b = '';
    if (!a && !b) return 0;
    if (!a) return -1;
    if (!b) return 1;
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  };

  getHeaderId(fieldName: string) {
    const validFieldName = fieldName.replace(/[^\w-]/g, '');
    return `table-header-${validFieldName}`;
  }

  handleIconClick(icon: DatatableColumnIcon<T>, dataRow: T, event: MouseEvent) {
    if (icon.onClick) {
      event.stopPropagation();
      if (icon.doDisable?.(dataRow)) return;
      icon.onClick(dataRow, event);
    }
  }

  private getSingleSelectedId(dataRow: T, forHighlight = false) {
    const { key, single, highlightKey } = this.datatableConfig()?.select ?? {};
    const selectBy = key ?? single ?? (forHighlight ? highlightKey : undefined);
    if (selectBy) return dataRow[selectBy];
  }

  getMultiSelectedId(dataRow: T) {
    const { key, multi } = this.datatableConfig()?.select ?? {};
    const selectBy = key ?? multi;
    if (selectBy) return dataRow[selectBy];
  }

  getMultiSelectedIds() {
    const { key, multi } = this.datatableConfig()?.select ?? {};
    const selectBy = key ?? multi;
    if (selectBy) return this.data()?.map((row) => row[selectBy]);
  }

  private handleRangeCheckboxToggle({
    checked,
    shiftKey,
    selectionId,
  }: DatatableCheckboxToggleEvent) {
    const { canSelect } = this.datatableConfig()?.select ?? {};
    if (!this.isValidSelectionId(selectionId)) return;
    if (canSelect && !canSelect()) return;

    const idsInOrder = this.getRangeSelectionIdsInOrder();
    const selectedIds = this.getCurrentSelectedIds();
    const { selectIds, deselectIds } = this.rangeSelectionController.handleToggle(
      selectionId,
      shiftKey,
      checked,
      idsInOrder,
      selectedIds,
    );
    if (selectIds.length) this.tableSelectionService.selectMultiple(selectIds);
    if (deselectIds.length) this.tableSelectionService.deselectMultiple(deselectIds);
  }

  private getRangeSelectionIdsInOrder(): (string | number)[] {
    const ids = this.sortedData()
      .map((row) => this.getMultiSelectedId(row))
      .filter((id) => id != null) as unknown[];
    return ids.filter((id): id is string | number => this.isValidSelectionId(id));
  }

  private getCurrentSelectedIds(): (string | number)[] {
    return (this.tableSelectionService.selectionSignal() as unknown[]).filter(
      (id): id is string | number => this.isValidSelectionId(id),
    );
  }

  private isValidSelectionId(id: unknown): id is string | number {
    return typeof id === 'number' || typeof id === 'string';
  }

  isSingleSelected(dataRow: T, forHighlight = false) {
    const selectedId = this.getSingleSelectedId(dataRow, forHighlight);
    if (selectedId != null) return this.tableSelectionService.isSingleSelected(selectedId);
  }

  isRowHighlighted(dataRow: T) {
    if (this.tableSelectionService.highlightedRow() === dataRow) {
      return true;
    }
    const key = this.datatableConfig()?.select?.key;
    return (
      key &&
      this.tableSelectionService.highlightedRow() &&
      dataRow &&
      this.tableSelectionService.highlightedRow()[key] === dataRow[key]
    );
  }

  isRowMultiHighlighted(dataRow: T) {
    return this.tableSelectionService.highlightedRowsSet?.()?.has(dataRow) ?? false;
  }

  isInMultiSelect(dataRow: T) {
    const config = this.datatableConfig();
    if (config.columns?.[0]?.component?.name !== 'DatatableCheckboxComponent') return;
    const selectedId = this.getMultiSelectedId(dataRow);
    return this.tableSelectionService.isSelected(selectedId);
  }

  protected readonly componentsThatEmitOnClick = componentsThatEmitOnClick;

  showCustomCellTooltip(event: MouseEvent, column: DatatableColumn<T>, row: T) {
    const isComponentTooltip = Boolean(column.cellHoverComponent);
    if (!this.datatableConfig().useCustomTooltip && !isComponentTooltip) {
      this.cellTooltipVisible.set(false);
      return;
    }
    const htmlElement = event.currentTarget as HTMLElement;
    const rect = htmlElement.getBoundingClientRect();
    const tooltip = isComponentTooltip ? '' : this.datatableTooltipPipe.transform(row, column);
    if (!isComponentTooltip && !tooltip) {
      this.cellTooltipVisible.set(false);
      return;
    }

    const estimatedHeight = isComponentTooltip ? 200 : 50;
    const estimatedWidth = isComponentTooltip ? 290 : 200;
    const isAbove =
      window.innerHeight - rect.bottom < estimatedHeight && rect.top > estimatedHeight;

    this.cellTooltipComponent.set(isComponentTooltip ? column.cellHoverComponent(row) : undefined);
    this.cellTooltipRow.set(isComponentTooltip ? row : undefined);
    this.cellTooltipText.set(tooltip);
    this.cellTooltipAbove.set(isAbove);
    this.cellTooltipTop.set(isAbove ? rect.top - 4 : rect.bottom + 4);
    this.cellTooltipLeft.set(
      Math.max(8, Math.min(rect.left, window.innerWidth - estimatedWidth - 16)),
    );
    this.cellTooltipClass.set(column.tooltipClass);
    this.cellTooltipVisible.set(true);
  }

  hideCustomCellTooltip() {
    this.cellTooltipVisible.set(false);
    this.cellTooltipComponent.set(undefined);
    this.cellTooltipRow.set(undefined);
    this.cellTooltipAbove.set(false);
  }
}
